import { useState } from 'react'
import { Upload, CheckCircle, XCircle, AlertCircle, X, FileText } from 'lucide-react'
import { useImportCsv, useClientTags } from '@hooks/useClients'
import { useUIStore } from '@stores/ui'
import { Client } from '@/types'

interface CsvImportWizardProps {
  onClose: () => void
  onSuccess: () => void
}

type Step = 'upload' | 'options' | 'preview' | 'result'

export function CsvImportWizard({ onClose, onSuccess }: CsvImportWizardProps) {
  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [options, setOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
    defaultStatus: 'active' as Client['status'],
    tags: [] as string[],
  })
  const [previewData, setPreviewData] = useState<string[][]>([])
  const [importResult, setImportResult] = useState<any>(null)

  const importMutation = useImportCsv()
  const { data: availableTags } = useClientTags()
  const showToast = useUIStore((state) => state.showToast)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      // Validate file extension
      if (!selectedFile.name.endsWith('.csv')) {
        showToast('Please select a CSV file', 'warning')
        return
      }

      // Validate MIME type
      if (selectedFile.type && selectedFile.type !== 'text/csv' && selectedFile.type !== 'application/vnd.ms-excel') {
        showToast('Invalid file type. Please select a valid CSV file.', 'error')
        return
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
      if (selectedFile.size > maxSize) {
        showToast('File size exceeds 10MB limit. Please select a smaller file.', 'error')
        return
      }

      setFile(selectedFile)
      parsePreview(selectedFile)
    }
  }

  const parsePreview = async (csvFile: File) => {
    const text = await csvFile.text()
    const rows = text.split('\n').slice(0, 6) // Preview first 5 rows + header
    const parsed = rows.map((row) => row.split(',').map((cell) => cell.trim()))
    setPreviewData(parsed)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const droppedFile = e.dataTransfer.files[0]

    if (!droppedFile) return

    // Validate file extension
    if (!droppedFile.name.endsWith('.csv')) {
      showToast('Please drop a CSV file', 'warning')
      return
    }

    // Validate MIME type
    if (droppedFile.type && droppedFile.type !== 'text/csv' && droppedFile.type !== 'application/vnd.ms-excel') {
      showToast('Invalid file type. Please drop a valid CSV file.', 'error')
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes
    if (droppedFile.size > maxSize) {
      showToast('File size exceeds 10MB limit. Please drop a smaller file.', 'error')
      return
    }

    setFile(droppedFile)
    parsePreview(droppedFile)
  }

  const handleImport = async () => {
    if (!file) return

    setStep('result')
    try {
      const result = await importMutation.mutateAsync({ file, options })
      setImportResult(result)
      if (result.success > 0) {
        onSuccess()
      }
    } catch (error) {
      setImportResult({ success: 0, failed: file.size, error: 'Import failed' })
    }
  }

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">Upload CSV File</h3>
        <p className="mt-2 text-sm text-gray-600">
          Upload a CSV file with email addresses and optional client information
        </p>
      </div>

      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 hover:border-primary"
      >
        {file ? (
          <div className="text-center">
            <FileText className="mx-auto mb-4 h-12 w-12 text-primary" />
            <p className="text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
          </div>
        ) : (
          <>
            <Upload className="mb-4 h-12 w-12 text-gray-400" />
            <p className="mb-2 text-sm font-medium text-gray-700">
              Drop your CSV file here or click to browse
            </p>
            <p className="text-xs text-gray-500">CSV files only, max 10MB</p>
          </>
        )}
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
          id="csv-upload"
        />
        <label
          htmlFor="csv-upload"
          className="mt-4 cursor-pointer rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90"
        >
          {file ? 'Change File' : 'Choose File'}
        </label>
      </div>

      {previewData.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Preview (first 5 rows)</h4>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {previewData[0].map((header, i) => (
                    <th key={i} className="px-3 py-2 text-left font-medium text-gray-700">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {previewData.slice(1).map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-3 py-2 text-gray-600">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => setStep('options')}
          disabled={!file}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  )

  const renderOptionsStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">Import Options</h3>
        <p className="mt-2 text-sm text-gray-600">Configure how to handle the import</p>
      </div>

      <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="skipDuplicates"
            checked={options.skipDuplicates}
            onChange={(e) => setOptions({ ...options, skipDuplicates: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <div>
            <label htmlFor="skipDuplicates" className="text-sm font-medium text-gray-900">
              Skip duplicate emails
            </label>
            <p className="text-xs text-gray-600">
              Don't import clients with email addresses that already exist
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="updateExisting"
            checked={options.updateExisting}
            onChange={(e) => setOptions({ ...options, updateExisting: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <div>
            <label htmlFor="updateExisting" className="text-sm font-medium text-gray-900">
              Update existing clients
            </label>
            <p className="text-xs text-gray-600">
              Update information for clients that already exist
            </p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">Default Status</label>
          <select
            value={options.defaultStatus}
            onChange={(e) =>
              setOptions({ ...options, defaultStatus: e.target.value as Client['status'] })
            }
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option>
            <option value="blocked">Blocked</option>
          </select>
          <p className="mt-1 text-xs text-gray-600">
            Status assigned to imported clients (if not specified in CSV)
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">Add Tags</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {availableTags?.map((tag) => (
              <button
                key={tag}
                onClick={() =>
                  setOptions({
                    ...options,
                    tags: options.tags.includes(tag)
                      ? options.tags.filter((t) => t !== tag)
                      : [...options.tags, tag],
                  })
                }
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  options.tags.includes(tag)
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tag}
              </button>
            ))}
            <input
              type="text"
              placeholder="Add custom tag..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  const newTag = e.currentTarget.value.trim()
                  if (!options.tags.includes(newTag)) {
                    setOptions({ ...options, tags: [...options.tags, newTag] })
                  }
                  e.currentTarget.value = ''
                }
              }}
              className="rounded-full border border-gray-300 px-3 py-1 text-xs focus:border-primary focus:outline-none"
            />
          </div>
          <p className="mt-1 text-xs text-gray-600">
            Tags to automatically apply to imported clients
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => setStep('upload')}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleImport}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          Start Import
        </button>
      </div>
    </div>
  )

  const renderResultStep = () => {
    if (!importResult) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm font-medium text-gray-900">Importing clients...</p>
          <p className="text-xs text-gray-600">This may take a moment</p>
        </div>
      )
    }

    const hasErrors = importResult.failed > 0 || importResult.errors?.length > 0

    return (
      <div className="space-y-4">
        <div className="text-center">
          {hasErrors ? (
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
          ) : (
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">
            {hasErrors ? 'Import Completed with Warnings' : 'Import Successful!'}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{importResult.success}</p>
            <p className="text-xs text-gray-600">Imported</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{importResult.failed}</p>
            <p className="text-xs text-gray-600">Failed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-600">{importResult.skipped}</p>
            <p className="text-xs text-gray-600">Skipped</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{importResult.duplicates}</p>
            <p className="text-xs text-gray-600">Duplicates</p>
          </div>
        </div>

        {importResult.errors && importResult.errors.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-900">Errors</h4>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-4">
              <ul className="space-y-2">
                {importResult.errors.slice(0, 10).map((error: any, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                    <span className="text-red-800">
                      Row {error.row}: {error.email} - {error.error}
                    </span>
                  </li>
                ))}
                {importResult.errors.length > 10 && (
                  <li className="text-xs text-red-600">
                    ... and {importResult.errors.length - 10} more errors
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 hover:bg-gray-100"
        >
          <X size={20} />
        </button>

        {step === 'upload' && renderUploadStep()}
        {step === 'options' && renderOptionsStep()}
        {step === 'result' && renderResultStep()}
      </div>
    </div>
  )
}
